import { useEffect, useState, useRef } from 'react'

/**
 * WalletPassQR — renders a QR code and opens an SSE stream.
 *
 * 1. Calls GET /api/auth/wallet/challenge → gets challengeId + approveUrl
 * 2. Renders QR encoding approveUrl (user scans with Apple/Google Wallet pass)
 * 3. Opens SSE /api/auth/wallet/poll?c=challengeId
 * 4. On 'approved' event: calls onSuccess(token)
 * 5. On 'expired'/'denied': resets or calls onError
 */

interface Props {
  onSuccess: (token: string) => void
  onError?:  (reason: string) => void
}

interface Challenge {
  challengeId: string
  approveUrl:  string
  expiresAt:   number
  ttl:         number
}

// Inline QR generation using a lightweight approach (qrcode.js CDN or inline)
// We use the `qrcode` npm package loaded dynamically to avoid bundle impact
async function generateQRDataURL(text: string): Promise<string> {
  try {
    const QRCode = (await import('qrcode')).default
    return QRCode.toDataURL(text, {
      width:         220,
      margin:        2,
      color: { dark: '#9333EA', light: '#0A0E1A' }, // Security Purple on Deep Night
    })
  } catch {
    // Fallback: use Google Charts API (dev only)
    return `https://chart.googleapis.com/chart?cht=qr&chs=220x220&chl=${encodeURIComponent(text)}&chco=9333EA&chf=bg,s,0A0E1A`
  }
}

export default function WalletPassQR({ onSuccess, onError }: Props) {
  const [challenge,  setChallenge]  = useState<Challenge | null>(null)
  const [qrDataUrl,  setQrDataUrl]  = useState<string | null>(null)
  const [countdown,  setCountdown]  = useState(0)
  const [status,     setStatus]     = useState<'loading' | 'ready' | 'approved' | 'error'>('loading')
  const sseRef = useRef<EventSource | null>(null)

  const initChallenge = async () => {
    setStatus('loading')
    try {
      const res  = await fetch('/api/auth/wallet/challenge')
      const data = await res.json()
      if (!data.ok) throw new Error(data.error)

      setChallenge(data)
      setCountdown(data.ttl)

      const qr = await generateQRDataURL(data.approveUrl)
      setQrDataUrl(qr)
      setStatus('ready')
      openSSE(data.challengeId)
    } catch (err) {
      setStatus('error')
      onError?.('Failed to generate QR')
    }
  }

  const openSSE = (challengeId: string) => {
    sseRef.current?.close()
    const es = new EventSource(`/api/auth/wallet/poll?c=${challengeId}`)

    es.addEventListener('approved', (e) => {
      const data = JSON.parse((e as MessageEvent).data)
      setStatus('approved')
      es.close()
      onSuccess(data.token)
    })
    es.addEventListener('expired', () => { es.close(); initChallenge() })
    es.addEventListener('denied',  () => { es.close(); setStatus('error'); onError?.('denied') })
    es.onerror = () => { es.close() }

    sseRef.current = es
  }

  useEffect(() => { initChallenge(); return () => sseRef.current?.close() }, [])

  // Countdown timer
  useEffect(() => {
    if (!challenge || status !== 'ready') return
    const interval = setInterval(() => {
      const remaining = Math.max(0, challenge.expiresAt - Math.floor(Date.now() / 1000))
      setCountdown(remaining)
      if (remaining === 0) clearInterval(interval)
    }, 1000)
    return () => clearInterval(interval)
  }, [challenge, status])

  const mm = String(Math.floor(countdown / 60)).padStart(2, '0')
  const ss = String(countdown % 60).padStart(2, '0')

  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      gap:            '16px',
      padding:        '24px 0',
    }}>
      {/* Eyebrow */}
      <p style={{
        fontFamily:  'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:    '9px',
        fontWeight:  400,
        letterSpacing: '0.35em',
        textTransform: 'uppercase',
        color:       'rgba(147,51,234,0.6)',
        margin:      0,
      }}>
        USE LUKHAS PASS
      </p>

      {/* QR */}
      <div style={{
        width:        '220px',
        height:       '220px',
        background:   '#0A0E1A',
        border:       '1px solid rgba(147,51,234,0.25)',
        borderRadius: '12px',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        overflow:     'hidden',
      }}>
        {status === 'loading' && (
          <div style={{
            width:  '16px',
            height: '16px',
            border: '1px solid rgba(147,51,234,0.3)',
            borderTopColor: 'rgba(147,51,234,0.8)',
            borderRadius: '50%',
            animation: 'wallet-spin 0.8s linear infinite',
          }} />
        )}
        {status === 'ready' && qrDataUrl && (
          <img src={qrDataUrl} alt="Scan with your LUKHAS pass" width={200} height={200}
               style={{ borderRadius: '8px' }} />
        )}
        {status === 'approved' && (
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="18" stroke="rgba(147,51,234,0.5)" strokeWidth="1"/>
            <path d="M12 20l6 6 10-12" stroke="#9333EA" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>

      {/* Instructions */}
      {status === 'ready' && (
        <p style={{
          fontFamily:  'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:    '9px',
          fontWeight:  300,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color:       'rgba(255,255,255,0.35)',
          textAlign:   'center',
          margin:      0,
          maxWidth:    '200px',
          lineHeight:  1.7,
        }}>
          Open Apple Wallet · Find your LUKHΛS pass<br/>
          Scan this code with the barcode scanner
        </p>
      )}

      {/* Countdown */}
      {status === 'ready' && countdown > 0 && (
        <p style={{
          fontFamily:  'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:    '10px',
          fontWeight:  400,
          letterSpacing: '0.15em',
          color:       countdown < 60 ? 'rgba(239,68,68,0.6)' : 'rgba(100,116,139,0.45)',
          margin:      0,
        }}>
          Expires in {mm}:{ss}
        </p>
      )}

      {/* Regenerate */}
      {status === 'error' && (
        <button
          onClick={initChallenge}
          style={{
            fontFamily:  'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:    '9px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color:       'rgba(147,51,234,0.7)',
            background:  'none',
            border:      '1px solid rgba(147,51,234,0.2)',
            padding:     '6px 12px',
            borderRadius: '4px',
            cursor:      'pointer',
          }}
        >
          Generate new code
        </button>
      )}

      <style>{`@keyframes wallet-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
